import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, Sparkles, Users, Shield, Zap, Music, Camera, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useRef } from 'react'

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.3,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}

const glowPulse = {
  initial: { boxShadow: '0 0 20px rgba(255, 45, 123, 0.4)' },
  animate: {
    boxShadow: [
      '0 0 20px rgba(255, 45, 123, 0.4)',
      '0 0 40px rgba(255, 45, 123, 0.6)',
      '0 0 20px rgba(255, 45, 123, 0.4)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

const features = [
  {
    icon: Sparkles,
    title: 'AI-Powered Creation',
    description: 'Transform your photos with AI tools that amplify your creativity, not replace it.',
    gradient: 'from-[#FF2D7B] to-[#6B3FA0]',
    glow: 'rgba(255, 45, 123, 0.3)',
  },
  {
    icon: Users,
    title: 'Real Connections',
    description: 'Chronological feed. No algorithms. Just you and the people you actually care about.',
    gradient: 'from-[#6B3FA0] to-[#00D4FF]',
    glow: 'rgba(107, 63, 160, 0.3)',
  },
  {
    icon: Shield,
    title: 'Safety by Design',
    description: 'Built from the ground up to protect young people. No manipulation, no dark patterns.',
    gradient: 'from-[#00D4FF] to-[#4DFFB4]',
    glow: 'rgba(0, 212, 255, 0.3)',
  },
  {
    icon: Music,
    title: 'Music Visuals',
    description: 'Turn your favorite tracks into beat-synced, shareable art with neon aesthetics.',
    gradient: 'from-[#FF2D7B] to-[#00D4FF]',
    glow: 'rgba(255, 45, 123, 0.3)',
  },
]

// Film grain overlay component
function FilmGrain() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[100] opacity-[0.015]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
      }}
    />
  )
}

// Animated gradient orb
function GradientOrb({
  className,
  colors,
  delay = 0,
  duration = 20,
}: {
  className?: string
  colors: string[]
  delay?: number
  duration?: number
}) {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl ${className}`}
      style={{
        background: `radial-gradient(circle, ${colors[0]} 0%, ${colors[1]} 50%, transparent 70%)`,
      }}
      animate={{
        x: [0, 30, -20, 0],
        y: [0, -40, 20, 0],
        scale: [1, 1.1, 0.95, 1],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  )
}

// Scanline effect
function Scanlines() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[99] opacity-[0.03]"
      style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
      }}
    />
  )
}

// Neon text component
function NeonText({
  children,
  className = '',
  color = '#FF2D7B',
}: {
  children: React.ReactNode
  className?: string
  color?: string
}) {
  return (
    <span
      className={`relative ${className}`}
      style={{
        textShadow: `0 0 10px ${color}, 0 0 20px ${color}, 0 0 40px ${color}40`,
      }}
    >
      {children}
    </span>
  )
}

// Phone mockup component
function PhoneMockup({ scrollYProgress }: { scrollYProgress: any }) {
  const rotateX = useTransform(scrollYProgress, [0, 0.5], [15, 0])
  const rotateY = useTransform(scrollYProgress, [0, 0.5], [-10, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.9, 1])

  return (
    <motion.div
      style={{ rotateX, rotateY, scale, perspective: 1000 }}
      className="relative mx-auto w-[280px] md:w-[320px]"
    >
      {/* Phone glow */}
      <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-[#FF2D7B]/30 via-[#6B3FA0]/20 to-[#00D4FF]/30 blur-2xl" />

      {/* Phone frame */}
      <div className="relative rounded-[2.5rem] border-4 border-[#2a2a35] bg-[#12121A] p-2 shadow-2xl">
        {/* Notch */}
        <div className="absolute left-1/2 top-4 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-[#0A0A0F]" />

        {/* Screen */}
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0A0A0F]">
          {/* App UI mockup */}
          <div className="aspect-[9/19] p-4">
            {/* Status bar */}
            <div className="flex items-center justify-between text-[10px] text-white/60">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="h-2 w-4 rounded-sm bg-white/60" />
                <div className="h-2 w-2 rounded-full bg-white/60" />
              </div>
            </div>

            {/* App header */}
            <div className="mt-4 flex items-center justify-between">
              <img
                src="/logos/logo_dark_mark_1024.png"
                alt="KLASSIC"
                className="h-6 w-auto"
              />
              <div className="flex gap-2">
                <div className="h-6 w-6 rounded-full bg-[#1E1E28]" />
                <div className="h-6 w-6 rounded-full bg-[#1E1E28]" />
              </div>
            </div>

            {/* Feed posts */}
            <div className="mt-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.2 }}
                  className="rounded-xl bg-[#1E1E28]/80 p-3"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="h-8 w-8 rounded-full"
                      style={{
                        background: i === 1
                          ? 'linear-gradient(135deg, #FF2D7B, #6B3FA0)'
                          : i === 2
                          ? 'linear-gradient(135deg, #6B3FA0, #00D4FF)'
                          : 'linear-gradient(135deg, #00D4FF, #4DFFB4)',
                      }}
                    />
                    <div className="flex-1">
                      <div className="h-2 w-16 rounded bg-white/20" />
                      <div className="mt-1 h-1.5 w-10 rounded bg-white/10" />
                    </div>
                  </div>
                  <div
                    className="mt-2 h-24 rounded-lg"
                    style={{
                      background: i === 1
                        ? 'linear-gradient(135deg, #FF2D7B20, #6B3FA020)'
                        : i === 2
                        ? 'linear-gradient(135deg, #6B3FA020, #00D4FF20)'
                        : 'linear-gradient(135deg, #00D4FF20, #4DFFB420)',
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export function HomePage() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  })

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0A0A0F] text-[#FAFAFA]">
      <FilmGrain />
      <Scanlines />

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed left-0 right-0 top-0 z-50 backdrop-blur-md bg-[#0A0A0F]/80"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logos/logo_dark_mark_1024.png"
              alt="KLASSIC"
              className="h-10 w-auto"
            />
            <span
              className="font-display text-xl font-bold tracking-tight hidden sm:block"
              style={{
                background: 'linear-gradient(135deg, #FF2D7B, #6B3FA0, #00D4FF)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradient-shift 4s ease infinite',
              }}
            >
              KLASSIC
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-[#FAFAFA]/70 hover:text-[#FAFAFA] hover:bg-white/5"
              >
                Log in
              </Button>
            </Link>
            <Link to="/register">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#FF2D7B] to-[#6B3FA0] px-6 font-medium text-white transition-all hover:shadow-[0_0_30px_rgba(255,45,123,0.5)]"
                >
                  Get Started
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden pt-20">
        {/* Animated gradient orbs */}
        <GradientOrb
          className="left-[10%] top-[20%] h-[500px] w-[500px] opacity-40"
          colors={['#FF2D7B', '#6B3FA0']}
          delay={0}
        />
        <GradientOrb
          className="right-[5%] top-[40%] h-[400px] w-[400px] opacity-30"
          colors={['#6B3FA0', '#00D4FF']}
          delay={2}
          duration={25}
        />
        <GradientOrb
          className="bottom-[10%] left-[30%] h-[600px] w-[600px] opacity-20"
          colors={['#00D4FF', '#4DFFB4']}
          delay={4}
          duration={30}
        />

        {/* Grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            {/* Text content */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-center lg:text-left"
            >
              {/* Badge */}
              <motion.div variants={itemVariants}>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#FF2D7B]/30 bg-[#FF2D7B]/10 px-4 py-2 text-sm font-medium text-[#FF2D7B]">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF2D7B] opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF2D7B]" />
                  </span>
                  Launching Early 2026
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={itemVariants}
                className="mt-8 font-display text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl"
              >
                <span className="block">Your world.</span>
                <span
                  className="block"
                  style={{
                    background: 'linear-gradient(135deg, #FF2D7B 0%, #6B3FA0 50%, #00D4FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: '0 0 80px rgba(255, 45, 123, 0.3)',
                  }}
                >
                  Your vibe.
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                variants={itemVariants}
                className="mt-6 text-lg leading-relaxed text-[#A0A0B0] sm:text-xl"
              >
                The social platform designed for your generation. AI-powered creativity,
                chronological feed, zero manipulation.
                <span className="text-[#FAFAFA]"> Social, but classic.</span>
              </motion.p>

              {/* CTAs */}
              <motion.div
                variants={itemVariants}
                className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start"
              >
                <Link to="/register">
                  <motion.div
                    variants={glowPulse}
                    initial="initial"
                    animate="animate"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      size="lg"
                      className="group relative h-14 w-full overflow-hidden rounded-2xl bg-gradient-to-r from-[#FF2D7B] via-[#6B3FA0] to-[#00D4FF] px-8 text-lg font-semibold text-white sm:w-auto"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        Join the Waitlist
                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-[#00D4FF] via-[#6B3FA0] to-[#FF2D7B] opacity-0 transition-opacity group-hover:opacity-100" />
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/login">
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 w-full rounded-2xl border-[#2a2a35] bg-transparent px-8 text-lg text-[#FAFAFA] hover:border-[#FF2D7B]/50 hover:bg-[#FF2D7B]/10 sm:w-auto"
                  >
                    I have an account
                  </Button>
                </Link>
              </motion.div>

              {/* Trust indicators */}
              <motion.div
                variants={itemVariants}
                className="mt-12 flex flex-wrap items-center justify-center gap-6 lg:justify-start"
              >
                {[
                  { icon: Shield, label: 'Safety-first', color: '#4DFFB4' },
                  { icon: Sparkles, label: 'AI-powered', color: '#FF2D7B' },
                  { icon: Zap, label: 'No algorithms', color: '#00D4FF' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm"
                    style={{
                      borderColor: `${item.color}30`,
                      backgroundColor: `${item.color}10`,
                    }}
                  >
                    <item.icon className="h-4 w-4" style={{ color: item.color }} />
                    <span style={{ color: item.color }}>{item.label}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Phone mockup */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block"
            >
              <PhoneMockup scrollYProgress={scrollYProgress} />
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="flex flex-col items-center gap-2 text-[#64647A]"
          >
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <div className="h-10 w-6 rounded-full border-2 border-current p-1">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="h-2 w-full rounded-full bg-current"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="relative py-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="font-display text-4xl font-bold sm:text-5xl">
              Create without the{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #FF2D7B, #00D4FF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                noise
              </span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-[#A0A0B0]">
              Everything you need to express yourself. Nothing designed to exploit you.
            </p>
          </motion.div>

          <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group relative overflow-hidden rounded-3xl border border-[#2a2a35] bg-[#12121A] p-6"
                style={{
                  boxShadow: `0 0 0 rgba(255, 45, 123, 0)`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 20px 60px ${feature.glow}`
                  e.currentTarget.style.borderColor = feature.glow.replace('0.3', '0.5')
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 rgba(255, 45, 123, 0)`
                  e.currentTarget.style.borderColor = '#2a2a35'
                }}
              >
                {/* Icon */}
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${feature.gradient}`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>

                {/* Content */}
                <h3 className="mt-6 font-display text-xl font-semibold">{feature.title}</h3>
                <p className="mt-3 text-[#A0A0B0] leading-relaxed">{feature.description}</p>

                {/* Hover gradient overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 transition-opacity group-hover:opacity-5`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="relative overflow-hidden py-32">
        {/* Background glow */}
        <div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#FF2D7B]/10 via-[#6B3FA0]/10 to-[#00D4FF]/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Phone mockup for mobile */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <PhoneMockup scrollYProgress={scrollYProgress} />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 text-center lg:order-2 lg:text-left"
            >
              <h2 className="font-display text-4xl font-bold sm:text-5xl">
                Designed for{' '}
                <span
                  style={{
                    background: 'linear-gradient(135deg, #00D4FF, #6B3FA0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  your generation
                </span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#A0A0B0]">
                We built KLASSIC because you deserve better than what's out there.
                A platform that respects your time, protects your privacy, and
                lets you be authentically you.
              </p>

              <div className="mt-10 space-y-6">
                {[
                  { icon: Camera, title: 'AI Photo Tools', desc: 'Uncrop, vibes, and magic edits that enhance your creativity' },
                  { icon: Music, title: 'Music Visuals', desc: 'Beat-synced neon art from your favorite songs' },
                  { icon: MessageCircle, title: 'Real Messaging', desc: 'Private convos with people you actually know' },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex items-start gap-4 text-left"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF2D7B]/20 to-[#6B3FA0]/20 border border-[#FF2D7B]/20">
                      <item.icon className="h-6 w-6 text-[#FF2D7B]" />
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-semibold">{item.title}</h4>
                      <p className="mt-1 text-[#A0A0B0]">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="mx-auto max-w-4xl px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-[2.5rem] border border-[#2a2a35] bg-[#12121A] p-12 text-center md:p-16"
          >
            {/* Background gradients */}
            <div className="absolute -left-20 -top-20 h-60 w-60 rounded-full bg-[#FF2D7B]/20 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-[#00D4FF]/20 blur-3xl" />

            <div className="relative z-10">
              <motion.div
                animate={{
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF2D7B] to-[#6B3FA0]"
              >
                <Sparkles className="h-10 w-10 text-white" />
              </motion.div>

              <h2 className="font-display text-4xl font-bold sm:text-5xl">
                Ready to join?
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-[#A0A0B0]">
                Be among the first to experience social media done right.
                Early access members get exclusive perks and help shape the future.
              </p>

              <Link to="/register">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-10 inline-block"
                >
                  <Button
                    size="lg"
                    className="h-16 rounded-2xl bg-gradient-to-r from-[#FF2D7B] via-[#6B3FA0] to-[#00D4FF] px-12 text-xl font-semibold text-white shadow-[0_0_40px_rgba(255,45,123,0.4)] transition-all hover:shadow-[0_0_60px_rgba(255,45,123,0.6)]"
                  >
                    Join the Waitlist
                  </Button>
                </motion.div>
              </Link>

              <p className="mt-6 text-sm text-[#64647A]">
                Free forever. No credit card required.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1E1E28] py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-3">
              <img
                src="/logos/logo_dark_mark_1024.png"
                alt="KLASSIC"
                className="h-8 w-auto"
              />
              <span
                className="font-display text-lg font-bold"
                style={{
                  background: 'linear-gradient(135deg, #FF2D7B, #6B3FA0, #00D4FF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                KLASSIC
              </span>
              <span className="text-sm text-[#64647A]">© 2025</span>
            </div>
            <div className="flex gap-8 text-sm text-[#64647A]">
              {['About', 'Privacy', 'Terms', 'Safety', 'Contact'].map((link) => (
                <a
                  key={link}
                  href="#"
                  className="transition-colors hover:text-[#FAFAFA]"
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-[#64647A]">
            Social, but classic.
          </div>
        </div>
      </footer>

      {/* CSS for gradient animation */}
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  )
}
